import { useEffect, useRef, useState } from "react";
import { useSearchContext } from "../context/SearchContext";
import { useArtistAutocomplete } from "../hooks/useArtistAutocomplete";

const IATA = /^[A-Z]{3}$/;

export function SearchBar() {
  const { state, dispatch } = useSearchContext();
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const { results, loading } = useArtistAutocomplete(state.artist);

  // Close the dropdown when clicking outside the search field.
  useEffect(() => {
    function onClick(e: MouseEvent): void {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  function selectArtist(name: string): void {
    dispatch({ type: "SET_ARTIST", artist: name });
    setOpen(false);
    setActiveIndex(-1);
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>): void {
    if (!open || results.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % results.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => (i <= 0 ? results.length - 1 : i - 1));
    } else if (e.key === "Enter" && activeIndex >= 0) {
      e.preventDefault();
      selectArtist(results[activeIndex].name);
    } else if (e.key === "Escape") {
      setOpen(false);
      setActiveIndex(-1);
    }
  }

  const airportInvalid =
    state.preferences.origin_airport.length > 0 &&
    !IATA.test(state.preferences.origin_airport);

  const showDropdown = open && (results.length > 0 || loading);

  return (
    <div className="flex flex-col gap-3 md:flex-row md:items-start">
      <div ref={containerRef} className="relative flex-1">
        <input
          type="text"
          value={state.artist}
          onChange={(e) => {
            dispatch({ type: "SET_ARTIST", artist: e.target.value });
            setOpen(true);
            setActiveIndex(-1);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder="Search artist (e.g. Kendrick Lamar)"
          role="combobox"
          aria-expanded={showDropdown}
          aria-controls="artist-listbox"
          aria-autocomplete="list"
          autoComplete="off"
          className="w-full rounded-lg border border-slate-300 bg-white px-4 py-3 text-base focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
        />

        {showDropdown && (
          <ul
            id="artist-listbox"
            role="listbox"
            className="absolute z-20 mt-1 max-h-64 w-full overflow-auto rounded-lg border border-slate-200 bg-white shadow-lg"
          >
            {loading && results.length === 0 && (
              <li className="px-4 py-3 text-sm text-slate-400">Searching…</li>
            )}
            {results.map((a, i) => (
              <li
                key={a.id}
                role="option"
                aria-selected={i === activeIndex}
                onMouseDown={(e) => {
                  e.preventDefault();
                  selectArtist(a.name);
                }}
                onMouseEnter={() => setActiveIndex(i)}
                className={
                  "flex cursor-pointer items-center justify-between px-4 py-2 text-sm " +
                  (i === activeIndex ? "bg-brand-50" : "hover:bg-slate-50")
                }
              >
                <span className="text-slate-900">{a.name}</span>
                {a.upcoming_shows_count > 0 && (
                  <span className="ml-2 shrink-0 text-xs text-slate-500">
                    {a.upcoming_shows_count} show
                    {a.upcoming_shows_count > 1 ? "s" : ""}
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="md:w-44">
        <input
          type="text"
          value={state.preferences.origin_airport}
          onChange={(e) =>
            dispatch({
              type: "SET_PREFERENCES",
              preferences: {
                origin_airport: e.target.value
                  .toUpperCase()
                  .replace(/[^A-Z]/g, ""),
              },
            })
          }
          maxLength={3}
          placeholder="Home airport"
          aria-label="Home airport (3-letter IATA code)"
          aria-invalid={airportInvalid}
          className="w-full rounded-lg border border-slate-300 bg-white px-4 py-3 text-base uppercase tracking-wider focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30 aria-[invalid=true]:border-red-400"
        />
        {airportInvalid && (
          <p className="mt-1 text-xs text-red-600">Enter a 3-letter code</p>
        )}
      </div>
    </div>
  );
}
